import { Card } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Share2, FileText, Video, Headphones, BookOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useFavoritedResources } from './hooks/useFeaturedResources';
import type { FeaturedResource } from './api/resourcesApi';

type MyResourcesSectionProps = {
  onShareClick?: (resource: FeaturedResource) => void;
  onBrowseClick?: () => void;
};

const getResourceIcon = (type: FeaturedResource['type']) => {
  const iconClass = 'w-5 h-5';
  switch (type) {
    case 'ARTICLE':
      return <FileText className={iconClass} />;
    case 'VIDEO':
      return <Video className={iconClass} />;
    case 'PODCAST':
      return <Headphones className={iconClass} />;
    case 'GUIDE':
      return <BookOpen className={iconClass} />;
    default:
      return <FileText className={iconClass} />;
  }
};

const getResourceTypeColor = (type: FeaturedResource['type']) => {
  switch (type) {
    case 'ARTICLE':
      return 'bg-blue-100 text-blue-700 hover:bg-blue-100';
    case 'VIDEO':
      return 'bg-purple-100 text-purple-700 hover:bg-purple-100';
    case 'PODCAST':
      return 'bg-green-100 text-green-700 hover:bg-green-100';
    case 'GUIDE':
      return 'bg-orange-100 text-orange-700 hover:bg-orange-100';
    default:
      return 'bg-gray-100 text-gray-700 hover:bg-gray-100';
  }
};

export function MyResourcesSection({
  onShareClick,
  onBrowseClick,
}: MyResourcesSectionProps) {
  const navigate = useNavigate();
  const { data: resources, isLoading, isError } = useFavoritedResources();

  const handleResourceClick = (resource: FeaturedResource) => {
    navigate(resource.url);
  };

  const handleShareClick = (e: React.MouseEvent, resource: FeaturedResource) => {
    e.stopPropagation(); // Prevent card click when clicking share button
    onShareClick?.(resource);
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg text-gray-900">My Resources</h3>
        </div>
        <div className="text-center py-8 text-gray-500">
          Loading resources...
        </div>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg text-gray-900">My Resources</h3>
        </div>
        <div className="text-center py-8 text-red-600">
          Error loading resources. Please try again later.
        </div>
      </Card>
    );
  }

  const resourceCount = resources?.length || 0;
  const hasResources = resourceCount > 0;

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg text-gray-900">My Resources</h3>
          {hasResources && (
            <p className="text-sm text-gray-500 mt-1">
              {resourceCount} {resourceCount === 1 ? 'resource' : 'resources'}
            </p>
          )}
        </div>
        {hasResources && onBrowseClick && (
          <Button
            variant="outline"
            size="sm"
            onClick={onBrowseClick}
            className="gap-2"
          >
            <BookOpen className="w-4 h-4" />
            Browse All
          </Button>
        )}
      </div>

      {!hasResources ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-8 h-8 text-gray-400" />
          </div>
          <h4 className="text-gray-900 mb-2">No favorited resources yet</h4>
          <p className="text-sm text-gray-500 mb-6">
            Browse resources and favorite the ones you want to share with your clients
          </p>
          {onBrowseClick && (
            <Button
              onClick={onBrowseClick}
              className="bg-[#026857] hover:bg-[#025247] gap-2"
            >
              <BookOpen className="w-4 h-4" />
              Browse Resources
            </Button>
          )}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {resources?.map((resource) => (
            <div
              key={resource.id}
              className="border border-gray-200 rounded-lg overflow-hidden hover:border-[#026857] transition-colors group cursor-pointer"
              onClick={() => handleResourceClick(resource)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleResourceClick(resource);
                } else if (e.key === ' ') {
                  e.preventDefault();
                  handleResourceClick(resource);
                }
              }}
            >
              {resource.thumbnailUrl ? (
                <img
                  src={resource.thumbnailUrl}
                  alt={resource.title}
                  className="w-full h-40 object-cover"
                />
              ) : (
                <div className="w-full h-40 bg-gradient-to-br from-[#026857]/10 to-[#378F94]/10 flex items-center justify-center">
                  {getResourceIcon(resource.type)}
                </div>
              )}
              <div className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={getResourceTypeColor(resource.type)}>
                    {resource.type}
                  </Badge>
                  <span className="text-xs text-gray-500">{resource.category}</span>
                </div>
                <h4 className="text-sm font-medium text-gray-900 mb-2 line-clamp-2">
                  {resource.title}
                </h4>
                <p className="text-xs text-gray-600 mb-3 line-clamp-2">
                  {resource.description}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full gap-2 hover:bg-[#026857]/5 hover:text-[#026857] hover:border-[#026857]"
                  onClick={(e) => handleShareClick(e, resource)}
                >
                  <Share2 className="w-4 h-4" />
                  Share
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

